import { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronRight,
  Tag,
} from 'lucide-react';
import {
  getAllItemCategories,
  createItemCategory,
  updateItemCategory,
  deleteItemCategory,
} from '../utils/itemCategoryApi';
import { ApiValidationError } from '../types/auth';
import type { ItemCategory } from '../types/itemCategory';

interface ManageCategoriesModalProps {
  onClose: () => void;
}

interface FormState {
  name: string;
  parent_category_id: string;
  active: boolean;
}

const emptyForm = (): FormState => ({ name: '', parent_category_id: '', active: true });

const canEdit = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const ManageCategoriesModal = ({ onClose }: ManageCategoriesModalProps) => {
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllItemCategories();
      setCategories(data.sort((a, b) => a.fully_qualified_name.localeCompare(b.fully_qualified_name)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (showForm) {
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [showForm]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFieldErrors({});
    setError(null);
    setShowForm(true);
  };

  const openEdit = (cat: ItemCategory) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      parent_category_id: cat.parent_category_id ? String(cat.parent_category_id) : '',
      active: cat.active,
    });
    setFieldErrors({});
    setError(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
    setFieldErrors({});
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Name is required.';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }

    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    try {
      const payload = {
        name: form.name.trim(),
        parent_category_id: form.parent_category_id ? Number(form.parent_category_id) : null,
        active: form.active,
      };
      if (editingId) {
        await updateItemCategory(editingId, payload);
        setSuccess('Category updated.');
      } else {
        await createItemCategory(payload);
        setSuccess('Category created.');
      }
      cancelForm();
      await fetchCategories();
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const mapped: Record<string, string> = {};
        for (const [k, v] of Object.entries(err.errors)) {
          mapped[k] = Array.isArray(v) ? v[0] : String(v);
        }
        setFieldErrors(mapped);
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save category');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (cat: ItemCategory) => {
    if (!window.confirm(`Delete "${cat.fully_qualified_name}"? This cannot be undone.`)) return;
    setDeletingId(cat.id);
    setError(null);
    try {
      await deleteItemCategory(cat.id);
      setSuccess(`"${cat.name}" deleted.`);
      await fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    } finally {
      setDeletingId(null);
    }
  };

  // Parent options: exclude the category being edited and its descendants
  const parentOptions = editingId
    ? categories.filter((c) => {
        if (c.id === editingId) return false;
        // exclude descendants (their fully_qualified_name starts with the edited cat's name path)
        const editing = categories.find((x) => x.id === editingId);
        if (editing && c.fully_qualified_name.startsWith(editing.fully_qualified_name + ' > ')) return false;
        return true;
      })
    : categories;

  const editable = canEdit();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-slate-700" />
            <h2 className="text-lg font-semibold text-gray-900">Manage Categories</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* feedback */}
          {success && !showForm && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              <CheckCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{success}</span>
              <button type="button" onClick={() => setSuccess(null)}><X className="h-4 w-4" /></button>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{error}</span>
              <button type="button" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          {/* inline create / edit form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3">
              <p className="text-sm font-semibold text-gray-900">
                {editingId ? 'Edit category' : 'New category'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={nameRef}
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${
                      fieldErrors.name ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g. Electronics"
                  />
                  {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Parent category</label>
                  <select
                    value={form.parent_category_id}
                    onChange={(e) => setForm((f) => ({ ...f, parent_category_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">— None (top level) —</option>
                    {parentOptions
                      .filter((c) => c.level < 3)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.fully_qualified_name}
                        </option>
                      ))}
                  </select>
                  {fieldErrors.parent_category_id && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.parent_category_id}</p>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-slate-800"
                />
                Active
              </label>

              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
                >
                  {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {editingId ? 'Save changes' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={cancelForm}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* list */}
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading categories…
            </div>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">
              No categories yet. Create one to get started.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {categories.map((cat) => (
                <li key={cat.id} className="flex items-center gap-3 py-2.5">
                  {/* indent indicator */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    {Array.from({ length: cat.level - 1 }).map((_, i) => (
                      <span key={i} className="w-4 border-l-2 border-gray-200 h-5 inline-block" />
                    ))}
                    {cat.level > 1 && <ChevronRight className="h-3.5 w-3.5 text-gray-300 shrink-0" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{cat.name}</span>
                    {cat.parent && (
                      <span className="ml-1.5 text-xs text-gray-400">in {cat.parent.name}</span>
                    )}
                  </div>

                  {!cat.active && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                      Inactive
                    </span>
                  )}

                  {editable && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => openEdit(cat)}
                        className="p-1.5 rounded text-slate-500 hover:bg-slate-100"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        disabled={deletingId === cat.id}
                        onClick={() => handleDelete(cat)}
                        className="p-1.5 rounded text-red-400 hover:bg-red-50 disabled:opacity-50"
                      >
                        {deletingId === cat.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />
                        }
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* footer */}
        {editable && (
          <div className="px-6 py-4 border-t border-gray-200 shrink-0">
            <button
              type="button"
              onClick={openCreate}
              disabled={showForm}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              New Category
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCategoriesModal;
